import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '../lib/api';
import { getUser } from '../lib/auth';
import { getSocket } from '../lib/socket';
import { dmKey, groupKey, markSeen } from '../lib/seen';
import { notify } from '../lib/prefs';
import { formatLastSeen } from '../lib/format';
import MessageBubble from './MessageBubble';
import PermissionDialog from './PermissionDialog';
import { normalizePermission, resolveUrl } from '../lib/media';

// Normalise a raw server message (DM uses senderId, group uses userId) into a
// flat shape the UI renders.
function normalize(m, myId) {
  const sender = m.senderId || m.userId || {};
  const senderId =
    typeof sender === 'object' && sender !== null ? String(sender._id) : String(sender);
  return {
    _id: m._id,
    clientId: m.clientId || null,
    text: m.text || '',
    type: m.type || 'text',
    // GridFS / view-only URLs are API-relative; blob: previews pass through.
    fileUrl: resolveUrl(m.fileUrl || m.file?.url || null),
    fileName: m.fileName || m.file?.name || null,
    downloadPermission: normalizePermission(m.downloadPermission),
    senderId,
    senderName: (typeof sender === 'object' && sender?.name) || '',
    createdAt: m.createdAt || new Date().toISOString(),
    status: m.status || 'sent',
    mine: senderId === String(myId),
    optimistic: Boolean(m.optimistic),
  };
}

// Insert-or-update by _id (dedupes the socket echo of our own sends).
function upsert(list, msg) {
  const i = list.findIndex((m) => m._id === msg._id);
  if (i === -1) return [...list, msg];
  const next = list.slice();
  next[i] = { ...next[i], ...msg };
  return next;
}

export default function ConversationPane({ conversation, className = '', onBack }) {
  const me = getUser();
  const myId = me?.userId;
  const isGroup = conversation?.kind === 'group';
  const convId = isGroup ? conversation?.groupId : conversation?.chatId;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draft, setDraft] = useState('');
  const [peerTyping, setPeerTyping] = useState(false);
  const [presence, setPresence] = useState(null); // { isOnline, lastSeen }
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // File waiting for the sender's download-permission choice.
  const [pendingFile, setPendingFile] = useState(null);

  const scrollRef = useRef(null);
  const typingTimer = useRef(null);
  const sentTypingRef = useRef(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const scrollToBottom = useCallback((behavior = 'auto') => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // ── Load history + join room + subscribe to realtime ─────────────────────
  useEffect(() => {
    if (!conversation || !convId) return undefined;
    const socket = getSocket();
    let alive = true;

    setMessages([]);
    setPeerTyping(false);
    setPresence(null);
    setSearchOpen(false);
    setSearchTerm('');
    setLoading(true);

    const path = isGroup
      ? `/api/groups/${convId}/messages`
      : `/api/chats/${convId}/messages`;

    api
      .get(path, { params: { limit: 50 } })
      .then(({ data }) => {
        if (!alive) return;
        const list = (data.messages || []).map((m) => normalize(m, myId));
        setMessages(list);
        const newest = list[list.length - 1];
        if (newest) markSeen(isGroup ? groupKey(convId) : dmKey(conversation.otherUserId), Date.now());
      })
      .catch((err) => console.error('Failed to load messages:', err))
      .finally(() => {
        if (alive) setLoading(false);
        requestAnimationFrame(() => scrollToBottom());
      });

    // Join the room and, for DMs, tell the peer we've read + watch presence.
    if (isGroup) {
      socket.emit('group:join', { groupId: convId });
    } else {
      socket.emit('chat:join', { chatId: convId });
      socket.emit('chat:read', { chatId: convId });
      api.put(`/api/chats/${convId}/messages/read`).catch(() => {});
      if (conversation.otherUserId) {
        socket.emit('presence:subscribe', { targetUserId: conversation.otherUserId });
        socket.emit('presence:get', { targetUserId: conversation.otherUserId });
      }
    }

    // ── Realtime handlers ──
    const onNewMessage = (raw) => {
      const belongs = isGroup ? raw.groupId === convId : raw.chatId === convId;
      if (!belongs) return;
      const msg = normalize(raw, myId);
      setMessages((prev) => {
        // If this is our own echo, reconcile a still-pending optimistic row.
        const next = prev.some((m) => m.optimistic && m.clientId && m.text === msg.text && m.mine)
          ? prev.filter((m) => !(m.optimistic && m.text === msg.text && m.mine))
          : prev;
        return upsert(next, msg);
      });
      requestAnimationFrame(() => scrollToBottom('smooth'));
      // Incoming (not mine) → desktop notification + read pointer + tell peer.
      if (!msg.mine) {
        const who = isGroup ? `${msg.senderName || 'New message'} · ${conversation.title}` : conversation.title;
        notify(who, msg.text || 'Sent an attachment');
      }
      if (!msg.mine && !isGroup) {
        socket.emit('chat:read', { chatId: convId });
        api.put(`/api/chats/${convId}/messages/read`).catch(() => {});
      }
      markSeen(isGroup ? groupKey(convId) : dmKey(conversation.otherUserId), Date.now());
    };

    const onBulkDelivered = ({ messages: delivered }) => {
      if (!delivered?.length) return;
      const ids = new Set(delivered.filter((d) => d.chatId === convId).map((d) => String(d.messageId)));
      if (!ids.size) return;
      setMessages((prev) =>
        prev.map((m) => (ids.has(String(m._id)) && m.status === 'sent' ? { ...m, status: 'delivered' } : m))
      );
    };

    const onRead = ({ chatId, byUserId }) => {
      if (isGroup || chatId !== convId || String(byUserId) === String(myId)) return;
      setMessages((prev) => prev.map((m) => (m.mine ? { ...m, status: 'read' } : m)));
    };

    const onTyping = (data) => {
      const match = isGroup ? data.groupId === convId : data.chatId === convId;
      if (!match || String(data.userId) === String(myId)) return;
      setPeerTyping(Boolean(data.isTyping));
    };

    const onPresence = (data) => {
      if (isGroup || String(data.userId) !== String(conversation.otherUserId)) return;
      setPresence({ isOnline: data.isOnline, lastSeen: data.lastSeen });
    };

    // Delete-for-everyone: drop the message from the open chat live.
    const onMessageDeleted = ({ chatId, groupId, messageId }) => {
      const belongs = isGroup ? groupId === convId : chatId === convId;
      if (!belongs) return;
      setMessages((prev) => prev.filter((m) => String(m._id) !== String(messageId)));
    };

    socket.on(isGroup ? 'group:newMessage' : 'chat:newMessage', onNewMessage);
    socket.on(isGroup ? 'group:messageDeleted' : 'chat:messageDeleted', onMessageDeleted);
    socket.on('chat:bulkDelivered', onBulkDelivered);
    socket.on('chat:read', onRead);
    socket.on('typing', onTyping);
    socket.on('presence:update', onPresence);

    return () => {
      alive = false;
      socket.off(isGroup ? 'group:newMessage' : 'chat:newMessage', onNewMessage);
      socket.off(isGroup ? 'group:messageDeleted' : 'chat:messageDeleted', onMessageDeleted);
      socket.off('chat:bulkDelivered', onBulkDelivered);
      socket.off('chat:read', onRead);
      socket.off('typing', onTyping);
      socket.off('presence:update', onPresence);
      if (isGroup) {
        socket.emit('group:leave', { groupId: convId });
      } else {
        socket.emit('chat:leave', { chatId: convId });
        if (conversation.otherUserId) {
          socket.emit('presence:unsubscribe', { targetUserId: conversation.otherUserId });
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId, isGroup]);

  // ── Typing indicator (outgoing) ──────────────────────────────────────────
  const signalTyping = () => {
    if (!convId) return;
    const socket = getSocket();
    const payload = isGroup ? { groupId: convId } : { chatId: convId };
    if (!sentTypingRef.current) {
      socket.emit('typing:start', payload);
      sentTypingRef.current = true;
    }
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      socket.emit('typing:stop', payload);
      sentTypingRef.current = false;
    }, 1500);
  };

  // ── Send (optimistic) ────────────────────────────────────────────────────
  const send = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || !convId) return;
    setDraft('');

    const socket = getSocket();
    if (sentTypingRef.current) {
      socket.emit('typing:stop', isGroup ? { groupId: convId } : { chatId: convId });
      sentTypingRef.current = false;
    }

    const clientId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const optimistic = normalize(
      {
        _id: clientId,
        clientId,
        text,
        type: 'text',
        senderId: { _id: myId, name: me?.userName },
        createdAt: new Date().toISOString(),
        status: 'sending',
        optimistic: true,
      },
      myId
    );
    setMessages((prev) => [...prev, optimistic]);
    requestAnimationFrame(() => scrollToBottom('smooth'));

    try {
      const path = isGroup
        ? `/api/groups/${convId}/messages`
        : `/api/chats/${convId}/messages`;
      const { data } = await api.post(path, { text, type: 'text' });
      const real = normalize(data, myId);
      setMessages((prev) => {
        const withoutTemp = prev.filter((m) => m.clientId !== clientId);
        return withoutTemp.some((m) => m._id === real._id) ? withoutTemp : [...withoutTemp, real];
      });
    } catch (err) {
      console.error('Send failed:', err);
      setMessages((prev) =>
        prev.map((m) => (m.clientId === clientId ? { ...m, status: 'failed' } : m))
      );
    }
  };

  // ── Send an attachment (image / video / document), Telegram-web style ──────
  // Coarse type from the browser File mime — matches the server's type values
  // ('image' | 'video' | 'document') and how MessageBubble renders them.
  const fileType = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'document';
  };

  // Every attachment goes through the permission dialog first.
  const sendFile = (file) => {
    if (!file || !convId || uploading) return;
    setPendingFile(file);
  };

  const uploadAndSend = async (file, downloadPermission) => {
    if (!file || !convId || uploading) return;
    const type = fileType(file);
    // Local preview while the bytes upload (revoked once the real message lands).
    const previewUrl =
      type === 'image' || type === 'video' ? URL.createObjectURL(file) : null;

    const clientId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const optimistic = normalize(
      {
        _id: clientId,
        clientId,
        text: '',
        type,
        fileUrl: previewUrl,
        fileName: file.name,
        downloadPermission,
        senderId: { _id: myId, name: me?.userName },
        createdAt: new Date().toISOString(),
        status: 'sending',
        optimistic: true,
      },
      myId
    );
    setMessages((prev) => [...prev, optimistic]);
    requestAnimationFrame(() => scrollToBottom('smooth'));
    setUploading(true);

    try {
      // 1) Upload the bytes. The chat upload endpoint returns a public URL and
      //    works for both DMs and groups (images/videos → Cloudinary, any other
      //    file → GridFS). View-only uploads always go to GridFS and come back
      //    as a short-lived signed URL.
      const form = new FormData();
      form.append('downloadPermission', downloadPermission);
      form.append('file', file);
      const { data: up } = await api.post('/api/chats/upload', form, {
        timeout: 5 * 60 * 1000, // big files / cold start
      });
      const fileUrl = up.url || up.fileUrl;
      const fileName = up.fileName || file.name;
      if (!fileUrl) throw new Error('Upload returned no URL');

      // 2) Create the message referencing the uploaded URL.
      const path = isGroup
        ? `/api/groups/${convId}/messages`
        : `/api/chats/${convId}/messages`;
      const { data } = await api.post(path, { type, fileUrl, fileName, downloadPermission });
      const real = normalize(data, myId);
      setMessages((prev) => {
        const withoutTemp = prev.filter((m) => m.clientId !== clientId);
        return withoutTemp.some((m) => m._id === real._id)
          ? withoutTemp
          : [...withoutTemp, real];
      });
    } catch (err) {
      console.error('File send failed:', err);
      setMessages((prev) =>
        prev.map((m) => (m.clientId === clientId ? { ...m, status: 'failed' } : m))
      );
    } finally {
      setUploading(false);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be picked again
    if (file) sendFile(file);
  };

  // Paste an image straight into the composer (Telegram/WhatsApp-web style).
  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const blob = it.getAsFile();
        if (blob) {
          e.preventDefault();
          const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
          const file = new File([blob], `pasted_${Date.now()}.${ext}`, {
            type: blob.type,
          });
          sendFile(file);
        }
        return;
      }
    }
  };

  // ── Delete a message (own message, or any in a group you admin) ────────────
  const removeMessage = async (msg) => {
    if (!msg?._id || !convId) return;
    if (!window.confirm('Delete this message?')) return;

    // Optimistic removal; restore on failure.
    setMessages((prev) => prev.filter((m) => m._id !== msg._id));

    // A still-optimistic message was never persisted — nothing to call.
    if (msg.optimistic || String(msg._id).startsWith('tmp_')) return;

    try {
      const path = isGroup
        ? `/api/groups/${convId}/messages/${msg._id}`
        : `/api/chats/${convId}/messages/${msg._id}`;
      await api.delete(path);
      // The server broadcasts *:messageDeleted; our own list is already updated.
    } catch (err) {
      console.error('Delete failed:', err);
      // Put it back in chronological order.
      setMessages((prev) =>
        [...prev, msg].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        )
      );
    }
  };

  if (!conversation) {
    return (
      <section className={`w-full h-full grid place-items-center bg-slate-50 ${className}`}>
        <div className="text-center">
          <div className="text-5xl mb-3">💬</div>
          <p className="text-ink font-medium">Select a chat to start messaging</p>
          <p className="text-muted text-sm mt-1">Your conversations stay in sync with your phone.</p>
        </div>
      </section>
    );
  }

  const subtitle = peerTyping
    ? 'typing…'
    : isGroup
    ? 'Group'
    : presence?.isOnline
    ? 'online'
    : presence?.lastSeen
    ? formatLastSeen(presence.lastSeen)
    : '';

  const term = searchTerm.trim();
  const visible = term
    ? messages.filter((m) => (m.text || '').toLowerCase().includes(term.toLowerCase()))
    : messages;

  return (
    <section className={`w-full h-full flex flex-col bg-slate-50 ${className}`}>
      <header className="h-16 px-4 flex items-center gap-3 border-b border-slate-200 bg-white">
        {onBack && (
          <button onClick={onBack} className="md:hidden text-muted text-xl pr-1" title="Back">
            ←
          </button>
        )}
        {conversation.photoURL ? (
          <img src={conversation.photoURL} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-brand/20 text-brand grid place-items-center font-semibold">
            {(conversation.title || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink truncate">{conversation.title}</div>
          {subtitle && (
            <div className={`text-xs ${peerTyping ? 'text-brand' : 'text-muted'}`}>{subtitle}</div>
          )}
        </div>
        <button
          onClick={() => {
            setSearchOpen((v) => !v);
            setSearchTerm('');
          }}
          title="Search messages"
          className="w-9 h-9 grid place-items-center rounded-full hover:bg-slate-100 text-muted"
        >
          🔍
        </button>
      </header>

      {searchOpen && (
        <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center gap-2">
          <input
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search in conversation"
            className="flex-1 px-3 py-1.5 rounded-full bg-slate-100 text-sm text-ink placeholder:text-muted focus:outline-none"
          />
          {term && (
            <span className="text-xs text-muted shrink-0">
              {visible.length} match{visible.length === 1 ? '' : 'es'}
            </span>
          )}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-1">
        {loading ? (
          <div className="h-full grid place-items-center text-muted text-sm">Loading messages…</div>
        ) : visible.length === 0 ? (
          <div className="h-full grid place-items-center text-muted text-sm">
            {term ? 'No matching messages.' : 'No messages yet. Say hi 👋'}
          </div>
        ) : (
          visible.map((m) => (
            <MessageBubble
              key={m._id}
              message={m}
              showSender={isGroup && !m.mine}
              highlight={term}
              onDelete={m.mine ? () => removeMessage(m) : undefined}
            />
          ))
        )}
      </div>

      <PermissionDialog
        file={pendingFile}
        onCancel={() => setPendingFile(null)}
        onConfirm={(permission) => {
          const file = pendingFile;
          setPendingFile(null);
          uploadAndSend(file, permission);
        }}
      />

      <form onSubmit={send} className="p-3 border-t border-slate-200 bg-white flex items-center gap-2">
        {/* Hidden pickers: paperclip = any document, image button = image/video */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={onPickFile}
        />
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*,video/*"
          className="hidden"
          onChange={onPickFile}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          title="Attach a file"
          className="w-10 h-10 shrink-0 rounded-full grid place-items-center text-muted hover:bg-slate-100 disabled:opacity-40"
        >
          📎
        </button>
        <button
          type="button"
          onClick={() => imageInputRef.current?.click()}
          disabled={uploading}
          title="Send photo or video"
          className="w-10 h-10 shrink-0 rounded-full grid place-items-center text-muted hover:bg-slate-100 disabled:opacity-40"
        >
          🖼
        </button>
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            signalTyping();
          }}
          onPaste={onPaste}
          placeholder={uploading ? 'Uploading…' : 'Type a message'}
          disabled={uploading}
          className="flex-1 px-4 py-2.5 rounded-full bg-slate-100 text-sm text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={!draft.trim() || uploading}
          className="w-11 h-11 rounded-full bg-brand text-white grid place-items-center disabled:opacity-40 hover:bg-brand-dark transition-colors"
        >
          ➤
        </button>
      </form>
    </section>
  );
}
